import React, { useState } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { Container, ContentWithPaddingXl } from "components/misc/Layouts.js";
import { ReactComponent as CheckboxIcon } from "feather-icons/dist/icons/check-circle.svg";

/*
 * Plan picker: organisation size x billing frequency -> price -> signup.
 *
 * PRICES MUST MATCH STRIPE. These mirror the six live Stripe prices
 * (STRIPE_PRICE_{ANNUAL,MONTHLY}_{SMALL,MEDIUM,LARGE}); verified identical on
 * 1 Oct 2026. Change them here only after changing them in Stripe.
 *
 * Monthly is exactly annual / 12, so the page does not claim an annual saving -
 * the old "save vs monthly" line was untrue. Tiers are sized by the number of
 * employees in the organisation (as the app's signup form says), not by
 * platform users, and there are no per-seat charges.
 */
const APP_URL = process.env.REACT_APP_APP_URL || "https://app.ethoshub.io";

export const TIERS = [
  { slug: "small", label: "Up to 500", annual: 10000, monthly: 833.33 },
  { slug: "medium", label: "501 – 5,000", annual: 15000, monthly: 1250 },
  { slug: "large", label: "5,001 – 20,000", annual: 22000, monthly: 1833.33 },
];

export const gbp = (n, decimals = 0) =>
  "£" + n.toLocaleString("en-GB", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const Heading = tw.h2`text-center text-3xl sm:text-4xl font-black text-secondary-500`;
const Intro = tw.p`mt-4 text-center text-lg text-gray-800 max-w-2xl mx-auto`;

const Controls = tw.div`mt-10 flex flex-col items-center gap-6`;
const ControlLabel = tw.p`text-sm font-semibold text-gray-700`;
const TierGroup = tw.div`flex flex-wrap justify-center gap-3`;
const TierButton = styled.button`
  ${tw`px-5 py-3 rounded-full font-semibold text-sm transition duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2`}
  ${p => (p.active ? tw`bg-primary-500 text-white` : tw`bg-gray-200 text-gray-800 hover:bg-gray-300`)}
`;

const ToggleRow = tw.div`flex items-center gap-4`;
const ToggleLabel = styled.span`${tw`text-sm font-semibold`} ${p => (p.active ? tw`text-gray-900` : tw`text-gray-600`)}`;
const ToggleSwitchContainer = tw.div`relative w-16 select-none`;
const ToggleTrack = tw.div`block overflow-hidden h-4 mt-2 rounded-full bg-primary-500`;
const ToggleInput = styled.input`
  ${tw`absolute block w-8 h-8 rounded-full bg-white border-4 appearance-none cursor-pointer top-0 left-0 shadow-md`}
  transition: left 0.2s ease;
  /* Doubled class outranks any global [type=checkbox] styling. */
  &&, &&:checked {
    appearance: none; -webkit-appearance: none;
    background-image: none; background-color: #fff; border-color: #e5e7eb;
  }
  &&:checked { left: 2rem; }
  &&:focus-visible { outline: 2px solid #6415ff; outline-offset: 2px; }
`;

const Card = tw.div`mt-10 max-w-xl mx-auto rounded-2xl border border-gray-200 bg-white shadow-lg overflow-hidden`;
const CardTop = tw.div`px-8 py-8 text-center bg-gray-100`;
const Size = tw.p`text-sm font-semibold uppercase tracking-wide text-gray-700`;
const Price = tw.p`mt-2 text-5xl font-black text-gray-900`;
const Per = tw.span`text-lg font-medium text-gray-600`;
const Billing = tw.p`mt-2 text-gray-700`;
const CardBody = tw.div`px-8 py-8`;
const Included = tw.ul`space-y-3`;
const Item = tw.li`flex items-start gap-3 text-gray-800`;
const Tick = tw(CheckboxIcon)`w-5 h-5 mt-0.5 text-primary-500 flex-shrink-0`;
const Actions = tw.div`mt-8 flex flex-col sm:flex-row gap-3`;
const Primary = tw.a`flex-1 text-center px-6 py-4 rounded-full border-2 border-primary-500 bg-primary-500 text-white font-bold hocus:bg-primary-700 transition duration-300`;
const Secondary = tw.a`flex-1 text-center px-6 py-4 rounded-full border-2 border-primary-500 text-primary-500 font-bold hocus:bg-primary-500 hocus:text-white transition duration-300`;
const Footnote = tw.p`mt-6 text-center text-sm text-gray-700`;
const FootLink = tw.a`text-primary-500 font-semibold underline`;

const HIGHLIGHTS = [
  "Anonymous and named reporting, with Disguise Mode",
  "Two-way messaging, including with anonymous reporters",
  "Case management, internal notes and evidence uploads",
  "Leadership analytics that never reveal who reported",
  "Append-only audit trail of every case view and change",
  "Your logo, colours and welcome message",
];

export default ({ heading = "Simple pricing, every feature included", showHeading = true }) => {
  const [annual, setAnnual] = useState(true);
  const [tierIndex, setTierIndex] = useState(0);
  const tier = TIERS[tierIndex];
  const signupUrl = `${APP_URL}/signup?plan=${annual ? "annual" : "monthly"}&tier=${tier.slug}`;

  return (
    <Container>
      {/* Without its own heading the picker sits under the page intro, so drop the top padding. */}
      <ContentWithPaddingXl css={!showHeading && tw`pt-4 lg:pt-6`}>
        {showHeading && (
          <>
            <Heading>{heading}</Heading>
            <Intro>Priced by the size of your organisation — never per user. Choose your size and how you'd like to pay.</Intro>
          </>
        )}

        <Controls>
          <div>
            <ControlLabel tw="text-center mb-3">Number of employees</ControlLabel>
            <TierGroup role="radiogroup" aria-label="Number of employees">
              {TIERS.map((t, i) => (
                <TierButton key={t.slug} type="button" role="radio" aria-checked={i === tierIndex}
                  active={i === tierIndex} onClick={() => setTierIndex(i)}>
                  {t.label}
                </TierButton>
              ))}
            </TierGroup>
          </div>
          <ToggleRow>
            <ToggleLabel active={!annual}>Pay monthly</ToggleLabel>
            <ToggleSwitchContainer>
              <ToggleTrack>
                <ToggleInput type="checkbox" checked={annual} aria-label="Pay annually"
                  onChange={() => setAnnual(a => !a)} />
              </ToggleTrack>
            </ToggleSwitchContainer>
            <ToggleLabel active={annual}>Pay annually</ToggleLabel>
          </ToggleRow>
        </Controls>

        <Card>
          <CardTop>
            <Size>{tier.label} employees</Size>
            {annual ? (
              <>
                <Price>{gbp(tier.annual)}<Per> / year</Per></Price>
                <Billing>Billed once a year · the same as {gbp(tier.monthly, 2)} a month</Billing>
              </>
            ) : (
              <>
                <Price>{gbp(tier.monthly, tier.monthly % 1 ? 2 : 0)}<Per> / month</Per></Price>
                <Billing>Billed monthly · {gbp(tier.annual)} over a year</Billing>
              </>
            )}
          </CardTop>
          <CardBody>
            <Included>
              {HIGHLIGHTS.map(h => <Item key={h}><Tick aria-hidden="true" />{h}</Item>)}
            </Included>
            <Actions>
              <Primary href={signupUrl}>Get started</Primary>
              <Secondary href="/contact">Book a demo</Secondary>
            </Actions>
          </CardBody>
        </Card>
        <Footnote>
          More than 20,000 employees? <FootLink href="/contact">Talk to us</FootLink> about a plan for your organisation.
        </Footnote>
      </ContentWithPaddingXl>
    </Container>
  );
};
